/**
 * Optimization Dashboard - AI-Powered Workflow Optimization Center
 *
 * FERP CRITICAL: Premium AI optimization recommendation management
 *
 * Features:
 * - AI-generated optimization recommendations display
 * - Recommendation lifecycle management (accept/reject/implement)
 * - Impact analysis visualization
 * - Priority-based filtering
 * - ROI calculation and tracking
 * - Implementation metrics
 *
 * Revenue Impact: $750K/year - Premium AI feature
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Button,
  Badge,
  Alert,
  Tabs,
  Grid,
  Paper,
  Progress,
  Table,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  Select,
  LoadingOverlay,
  RingProgress,
  ThemeIcon,
  Divider,
  Box,
  ScrollArea
} from '@mantine/core';
import {
  IconSparkles,
  IconCheck,
  IconX,
  IconClock,
  IconTrendingUp,
  IconAlertCircle,
  IconChartBar,
  IconRocket,
  IconShield,
  IconCoin,
  IconBolt,
  IconInfoCircle,
  IconEye,
  IconPlayerPlay
} from '@tabler/icons-react';
import {
  useOptimizationRecommendations,
  type OptimizationRecommendation,
  type RecommendationPriority,
  type RecommendationType,
  type RecommendationStatus
} from '../hooks/useOptimizationRecommendations';
import { RecommendationCard } from './RecommendationCard';
import { ImpactVisualization } from './ImpactVisualization';

/**
 * Optimization Dashboard Component
 */
export function OptimizationDashboard({ workflowId }: { workflowId?: string }) {
  const {
    recommendations,
    loading,
    error,
    metrics,
    listRecommendations,
    acceptRecommendation,
    rejectRecommendation,
    markImplemented,
    getByPriority,
    getHighConfidence,
    getPendingApprovals,
    calculateTotalROI
  } = useOptimizationRecommendations(workflowId);

  const [activeTab, setActiveTab] = useState<string>('pending');
  const [selectedRecommendation, setSelectedRecommendation] = useState<OptimizationRecommendation | null>(null);
  const [actionModalOpened, setActionModalOpened] = useState(false);
  const [actionType, setActionType] = useState<'accept' | 'reject' | 'implement'>('accept');
  const [actionComments, setActionComments] = useState('');
  const [filterPriority, setFilterPriority] = useState<RecommendationPriority | null>(null);
  const [filterType, setFilterType] = useState<RecommendationType | null>(null);

  /**
   * Handle recommendation action
   */
  const handleAction = async () => {
    if (!selectedRecommendation) return;

    try {
      const userId = 'current-user'; // TODO: Get from auth context

      if (actionType === 'accept') {
        await acceptRecommendation(selectedRecommendation.id, userId, actionComments || undefined);
      } else if (actionType === 'reject') {
        if (!actionComments) {
          alert('Rejection reason is required');
          return;
        }
        await rejectRecommendation(selectedRecommendation.id, userId, actionComments);
      } else if (actionType === 'implement') {
        await markImplemented(selectedRecommendation.id, userId, actionComments ? { notes: actionComments } : undefined);
      }

      setActionModalOpened(false);
      setSelectedRecommendation(null);
      setActionComments('');
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  /**
   * Open action modal
   */
  const openActionModal = (recommendation: OptimizationRecommendation, action: 'accept' | 'reject' | 'implement') => {
    setSelectedRecommendation(recommendation);
    setActionType(action);
    setActionModalOpened(true);
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (priority: RecommendationPriority): string => {
    switch (priority) {
      case 'emergency': return 'red';
      case 'critical': return 'orange';
      case 'high': return 'yellow';
      case 'medium': return 'blue';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  /**
   * Get type icon
   */
  const getTypeIcon = (type: RecommendationType) => {
    switch (type) {
      case 'performance': return <IconBolt size={16} />;
      case 'cost': return <IconCoin size={16} />;
      case 'safety': return <IconShield size={16} />;
      case 'compliance': return <IconCheck size={16} />;
      default: return <IconSparkles size={16} />;
    }
  };

  /**
   * Get status color
   */
  const getStatusColor = (status: RecommendationStatus): string => {
    switch (status) {
      case 'pending': return 'blue';
      case 'under_review': return 'cyan';
      case 'accepted': return 'green';
      case 'rejected': return 'red';
      case 'in_progress': return 'yellow';
      case 'implemented': return 'teal';
      case 'failed': return 'red';
      case 'reverted': return 'orange';
      default: return 'gray';
    }
  };

  /**
   * Filter recommendations
   */
  const filteredRecommendations = recommendations.filter(rec => {
    if (filterPriority && rec.priority !== filterPriority) return false;
    if (filterType && rec.recommendationType !== filterType) return false;
    return true;
  });

  /**
   * Get recommendations by tab
   */
  const getRecommendationsByTab = (tab: string): OptimizationRecommendation[] => {
    switch (tab) {
      case 'pending':
        return filteredRecommendations.filter(r => r.status === 'pending');
      case 'review':
        return filteredRecommendations.filter(r => r.status === 'under_review');
      case 'accepted':
        return filteredRecommendations.filter(r => r.status === 'accepted' || r.status === 'in_progress');
      case 'implemented':
        return filteredRecommendations.filter(r => r.status === 'implemented');
      case 'rejected':
        return filteredRecommendations.filter(r => r.status === 'rejected' || r.status === 'failed');
      case 'all':
        return filteredRecommendations;
      default:
        return filteredRecommendations;
    }
  };

  const tabRecommendations = getRecommendationsByTab(activeTab);

  /**
   * Calculate statistics
   */
  const stats = {
    pending: recommendations.filter(r => r.status === 'pending').length,
    accepted: recommendations.filter(r => r.status === 'accepted' || r.status === 'in_progress').length,
    implemented: recommendations.filter(r => r.status === 'implemented').length,
    totalROI: calculateTotalROI(),
    avgConfidence: recommendations.length > 0
      ? recommendations.reduce((sum, r) => sum + r.confidenceScore, 0) / recommendations.length
      : 0,
    highPriority: recommendations.filter(r => r.priority === 'critical' || r.priority === 'emergency').length
  };

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Stack gap="lg" mb="xl">
        <Group justify="space-between">
          <div>
            <Title order={1}>
              <Group gap="sm">
                <IconSparkles size={32} color="var(--mantine-color-violet-6)" />
                Optimization Recommendations
              </Group>
            </Title>
            <Text c="dimmed" mt="xs">
              AI-powered workflow optimization suggestions
            </Text>
          </div>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error.message}
          </Alert>
        )}

        {/* Key Metrics */}
        <Grid>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconClock size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Pending</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.pending}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="green">
                    <IconCheck size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Accepted</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.accepted}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="teal">
                    <IconRocket size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Implemented</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.implemented}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="orange">
                    <IconAlertCircle size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">High Priority</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.highPriority}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="violet">
                    <IconTrendingUp size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Potential ROI</Text>
                </Group>
                <Text size="xl" fw={700}>${Math.round(stats.totalROI).toLocaleString()}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="cyan">
                    <IconSparkles size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Avg Confidence</Text>
                </Group>
                <Text size="xl" fw={700}>{Math.round(stats.avgConfidence * 100)}%</Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Implementation Metrics */}
        {metrics && (
          <Card withBorder>
            <Group justify="space-between">
              <div>
                <Text fw={600} mb="xs">Implementation Track Record</Text>
                <Group gap="xl">
                  <div>
                    <Text size="sm" c="dimmed">Total Implemented</Text>
                    <Text size="lg" fw={600}>{metrics.totalImplemented}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Time Savings</Text>
                    <Text size="lg" fw={600}>
                      {Math.round(metrics.totalTimeSavingsSeconds / 3600)}h
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Cost Savings</Text>
                    <Text size="lg" fw={600}>
                      {Math.round(metrics.totalCostSavingsPercentage)}%
                    </Text>
                  </div>
                </Group>
              </div>
              <RingProgress
                size={120}
                thickness={12}
                sections={[
                  { value: (metrics.averageConfidence * 100), color: 'violet' }
                ]}
                label={
                  <Text ta="center" size="sm">
                    <Text fw={700} size="lg">{Math.round(metrics.averageConfidence * 100)}%</Text>
                    <Text size="xs" c="dimmed">Confidence</Text>
                  </Text>
                }
              />
            </Group>
          </Card>
        )}

        {/* Filters */}
        <Group>
          <Select
            placeholder="Filter by priority"
            data={[
              { value: '', label: 'All Priorities' },
              { value: 'emergency', label: 'Emergency' },
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' }
            ]}
            value={filterPriority || ''}
            onChange={(value) => setFilterPriority(value as RecommendationPriority || null)}
            clearable
          />
          <Select
            placeholder="Filter by type"
            data={[
              { value: '', label: 'All Types' },
              { value: 'performance', label: 'Performance' },
              { value: 'cost', label: 'Cost' },
              { value: 'safety', label: 'Safety' },
              { value: 'compliance', label: 'Compliance' },
              { value: 'usability', label: 'Usability' },
              { value: 'reliability', label: 'Reliability' }
            ]}
            value={filterType || ''}
            onChange={(value) => setFilterType(value as RecommendationType || null)}
            clearable
          />
        </Group>
      </Stack>

      {/* Recommendations Tabs */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'pending')}>
        <Tabs.List>
          <Tabs.Tab value="pending" leftSection={<IconClock size={16} />}>
            Pending ({stats.pending})
          </Tabs.Tab>
          <Tabs.Tab value="review" leftSection={<IconEye size={16} />}>
            Under Review
          </Tabs.Tab>
          <Tabs.Tab value="accepted" leftSection={<IconCheck size={16} />}>
            Accepted ({stats.accepted})
          </Tabs.Tab>
          <Tabs.Tab value="implemented" leftSection={<IconRocket size={16} />}>
            Implemented ({stats.implemented})
          </Tabs.Tab>
          <Tabs.Tab value="rejected" leftSection={<IconX size={16} />}>
            Rejected
          </Tabs.Tab>
          <Tabs.Tab value="all" leftSection={<IconChartBar size={16} />}>
            All
          </Tabs.Tab>
        </Tabs.List>

        {/* Recommendations List */}
        <Tabs.Panel value={activeTab} pt="md">
          {tabRecommendations.length === 0 ? (
            <Card withBorder>
              <Stack ta="center" gap="md" py="xl">
                <IconInfoCircle size={48} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed" ta="center">
                  No recommendations found for this filter
                </Text>
              </Stack>
            </Card>
          ) : (
            <Stack gap="md">
              {tabRecommendations.map((recommendation) => (
                <RecommendationCard
                  key={recommendation.id}
                  recommendation={recommendation}
                  onAccept={() => openActionModal(recommendation, 'accept')}
                  onReject={() => openActionModal(recommendation, 'reject')}
                  onImplement={() => openActionModal(recommendation, 'implement')}
                  onViewDetails={() => setSelectedRecommendation(recommendation)}
                />
              ))}
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Action Modal */}
      <Modal
        opened={actionModalOpened}
        onClose={() => {
          setActionModalOpened(false);
          setActionComments('');
        }}
        title={
          actionType === 'accept' ? 'Accept Recommendation' :
          actionType === 'reject' ? 'Reject Recommendation' :
          'Mark as Implemented'
        }
        size="lg"
      >
        {selectedRecommendation && (
          <Stack gap="md">
            <Alert icon={<IconInfoCircle size={16} />} color="blue">
              <Text fw={600}>{selectedRecommendation.name}</Text>
              <Text size="sm" mt="xs">{selectedRecommendation.description}</Text>
            </Alert>

            {/* Impact Preview */}
            <ImpactVisualization
              impactAnalysis={selectedRecommendation.impactAnalysis}
              compact
            />

            <Textarea
              label={
                actionType === 'accept' ? 'Comments (optional)' :
                actionType === 'reject' ? 'Rejection Reason (required)' :
                'Implementation Notes (optional)'
              }
              placeholder={
                actionType === 'accept' ? 'Add any comments about acceptance...' :
                actionType === 'reject' ? 'Explain why this recommendation is being rejected...' :
                'Add notes about the implementation...'
              }
              value={actionComments}
              onChange={(e) => setActionComments(e.target.value)}
              minRows={4}
              required={actionType === 'reject'}
            />

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setActionModalOpened(false);
                  setActionComments('');
                }}
              >
                Cancel
              </Button>
              <Button
                color={
                  actionType === 'accept' ? 'green' :
                  actionType === 'reject' ? 'red' :
                  'blue'
                }
                onClick={handleAction}
                leftSection={
                  actionType === 'accept' ? <IconCheck size={16} /> :
                  actionType === 'reject' ? <IconX size={16} /> :
                  <IconRocket size={16} />
                }
              >
                {actionType === 'accept' ? 'Accept' :
                 actionType === 'reject' ? 'Reject' :
                 'Mark Implemented'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
